import React, { useState, useEffect } from 'react';
import ApiClient from '../api/client';
import { AlertIcon } from './icons';

interface ConvertKitFieldMappingEditorProps {
  formId: number;
  apiClient: ApiClient;
  hasConvertkitChannels: boolean;
  onMappingUpdated?: () => void;
}

interface FieldMap {
  email?: string;
  first_name?: string;
  [key: string]: string | object | undefined;
  custom_fields?: Record<string, string>;
}

const ConvertKitFieldMappingEditor: React.FC<ConvertKitFieldMappingEditorProps> = ({
  formId,
  apiClient,
  hasConvertkitChannels,
  onMappingUpdated,
}) => {
  const [fieldMap, setFieldMap] = useState<FieldMap>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [newCustomField, setNewCustomField] = useState({ name: '', value: '' });

  useEffect(() => {
    if (hasConvertkitChannels) {
      loadFieldMapping();
    }
  }, [formId, hasConvertkitChannels]);

  const loadFieldMapping = async () => {
    setLoading(true);
    try {
      const result = await apiClient.getConvertkitFieldMapping(formId);
      setFieldMap(result.fieldMap || {});
      setError(null);
    } catch (err) {
      // Field mapping might not exist yet (404), which is fine
      setFieldMap({});
      setError(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEmailChange = (value: string) => {
    setFieldMap({ ...fieldMap, email: value });
    setIsDirty(true);
  };

  const handleFirstNameChange = (value: string) => {
    if (value === '') {
      const updated = { ...fieldMap };
      delete updated.first_name;
      setFieldMap(updated);
    } else {
      setFieldMap({ ...fieldMap, first_name: value });
    }
    setIsDirty(true);
  };

  const handleCustomFieldChange = (key: string, value: string) => {
    const customFields = fieldMap.custom_fields || {};
    if (value === '') {
      const updated = { ...customFields };
      delete updated[key];
      setFieldMap({
        ...fieldMap,
        custom_fields: Object.keys(updated).length > 0 ? updated : undefined,
      });
    } else {
      setFieldMap({
        ...fieldMap,
        custom_fields: { ...customFields, [key]: value },
      });
    }
    setIsDirty(true);
  };

  const handleAddCustomField = () => {
    if (!newCustomField.name.trim() || !newCustomField.value.trim()) {
      setError('Please enter both field name and mapping');
      return;
    }

    const customFields = fieldMap.custom_fields || {};
    if (customFields[newCustomField.name]) {
      setError('This custom field already exists');
      return;
    }

    setFieldMap({
      ...fieldMap,
      custom_fields: { ...customFields, [newCustomField.name]: newCustomField.value },
    });
    setNewCustomField({ name: '', value: '' });
    setIsDirty(true);
    setError(null);
  };

  const handleSave = async () => {
    if (!fieldMap.email) {
      setError('Email field mapping is required');
      return;
    }

    setSaving(true);
    try {
      await apiClient.setConvertkitFieldMapping(formId, fieldMap);
      setIsDirty(false);
      setError(null);
      onMappingUpdated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save field mapping');
    } finally {
      setSaving(false);
    }
  };

  if (!hasConvertkitChannels) {
    return null;
  }

  if (loading) {
    return (
      <div className="details-section">
        <h4>ConvertKit Field Mapping</h4>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <div className="loading-spinner"></div>
          <p>Loading field mapping...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="details-section">
      <h4>ConvertKit Field Mapping</h4>
      {error && (
        <div className="alert alert-error">
          <AlertIcon size={15} />
          <span>{error}</span>
        </div>
      )}

      <div className="editor-intro">
        <p>
          Map your form fields to ConvertKit fields. This mapping is shared across all ConvertKit channels attached to this form.
        </p>
      </div>

      <div className="form-group">
        <label>Email Field * (required)</label>
        <input
          type="text"
          value={fieldMap.email || ''}
          onChange={(e) => handleEmailChange(e.target.value)}
          placeholder="e.g., email_address, user_email"
          disabled={saving}
          className="mono-input"
        />
        <small>The form field that contains the subscriber's email</small>
      </div>

      <div className="form-group">
        <label>First Name Field (optional)</label>
        <input
          type="text"
          value={fieldMap.first_name || ''}
          onChange={(e) => handleFirstNameChange(e.target.value)}
          placeholder="e.g., first_name, fname"
          disabled={saving}
          className="mono-input"
        />
        <small>Direct field on ConvertKit subscriber (separate from custom fields)</small>
      </div>

      {fieldMap.custom_fields && Object.keys(fieldMap.custom_fields).length > 0 && (
        <div className="form-group">
          <label>Custom Fields</label>
          <div className="mapping-list">
            {Object.entries(fieldMap.custom_fields).map(([key, value]) => (
              <div key={key} className="mapping-row">
                <input
                  type="text"
                  value={key}
                  disabled
                  className="mono-input mapping-input"
                />
                <span className="mapping-arrow" aria-hidden="true">→</span>
                <input
                  type="text"
                  value={value}
                  onChange={(e) => handleCustomFieldChange(key, e.target.value)}
                  disabled={saving}
                  className="mono-input mapping-input"
                />
                <button
                  type="button"
                  onClick={() => handleCustomFieldChange(key, '')}
                  disabled={saving}
                  className="icon-btn intent-danger"
                  aria-label="Remove mapping"
                  title="Remove mapping"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="form-group">
        <label>Add Custom Field</label>
        <div className="mapping-row mapping-add-row">
          <input
            type="text"
            value={newCustomField.name}
            onChange={(e) => setNewCustomField({ ...newCustomField, name: e.target.value })}
            placeholder="ConvertKit field name"
            disabled={saving}
            className="mono-input mapping-input"
          />
          <span className="mapping-arrow" aria-hidden="true">→</span>
          <input
            type="text"
            value={newCustomField.value}
            onChange={(e) => setNewCustomField({ ...newCustomField, value: e.target.value })}
            placeholder="Form field name"
            disabled={saving}
            className="mono-input mapping-input"
          />
        </div>
        <button
          type="button"
          onClick={handleAddCustomField}
          disabled={saving}
          className="btn btn-sm btn-secondary"
        >
          + Add Field
        </button>
      </div>

      {isDirty && (
        <div className="editor-actions">
          <button
            onClick={handleSave}
            disabled={saving || !fieldMap.email}
            className="btn btn-primary editor-save"
          >
            {saving ? 'Saving...' : 'Save Mapping'}
          </button>
          <button
            onClick={loadFieldMapping}
            disabled={saving}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
};

export default ConvertKitFieldMappingEditor;
